import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AddUserModal from 'components/AddUserModal/AddUserModal';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import {
  TASK_ASSIGNED_TO,
  TASK_DURATION, TASK_END_DATE,
  TASK_RELATE_TO, TASK_START_DATE,
} from 'constants/formFields';
import {
  DATE_CALCULATIONS, TAB_TYPES, TASK_TYPE, TASK_TYPE_ACTION,
} from 'constants/general';
import { BUTTON_VARIANT, PRESIGNED_FILE_TYPES, TASK_STATUS } from 'constants/interfaces';
import useFileStorageUpload from 'hooks/useFileStorageUpload';
import {
  useCreateFileAttachmentMutation,
  useCreateTaskMutation, useDeleteFileAttachmentMutation, useUpdateSignToTaskMutation, useUpdateTaskMutation,
} from 'store/slices/tasksManagement/apis/tasksManagementApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { extractErrorMessage } from 'utils/helpers';

import useCreateTaskForm from '../hooks/useCreateTaskForm';
import TaskFormFields from './TaskFormFields';
import TaskRelatedModal from './TaskRelatedModal';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type { TPresignedFileTypes } from 'constants/interfaces';
import type IFileUpload from 'store/slices/tasksManagement/interfaces/IFileUpload';
import type ITaskCreateRequest from 'store/slices/tasksManagement/interfaces/ITaskCreateRequest';
import type ITaskUpdateRequest from 'store/slices/tasksManagement/interfaces/ITaskUpdateRequest';
import type ICreateTaskModal from '../interfaces/ICreateTaskModal';
import type { TTabTypes } from '../interfaces/ITTabTypes';

import styles from '../sass/CreateTaskModal.module.scss';

function CreateTaskModal({
  onClose, open, typeModal, itemData, attachments, hideFieldsForm, accessDeleteFile, currentStatus,
}: ICreateTaskModal) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeUserModal, setActiveUserModal] = useState(false);
  const [activeVcToModal, setActiveVcToModal] = useState(false);
  const [attachmentsFiles, setAttachmentsFiles] = useState<IFileUpload[]>([]);
  const { data: userData } = useUserMeQuery();
  const { uploadToStorage } = useFileStorageUpload();
  const [deleteFileAttachment] = useDeleteFileAttachmentMutation();
  const [createFileAttachment] = useCreateFileAttachmentMutation();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    watch,
  } = useCreateTaskForm({
    itemData,
    typeModal,
  });
  const [selectedTab, setSelectedTab] = useState<TTabTypes>(TAB_TYPES.VALUE_CHAIN);

  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [updateSignToTask] = useUpdateSignToTaskMutation();

  useEffect(() => {
    if (attachments) {
      setAttachmentsFiles(attachments);
    }
  }, [attachments, open]);

  const onOpenUserModal = () => {
    setActiveUserModal(true);
  };

  const onCloseUserModal = () => {
    setActiveUserModal(false);
  };

  const onOpenVcToModal = () => {
    setActiveVcToModal(true);
  };

  const onCloseVcToModal = () => {
    setActiveVcToModal(false);
  };

  const uploadFile = useCallback((file: File | string, fileType: TPresignedFileTypes) => {
    if (typeof file === 'object') {
      return uploadToStorage(file, fileType)
        .then((presignedData) => {
          setAttachmentsFiles((prev) => [...prev, {
            fileName: file.name,
            originalFileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
            fileUrl: presignedData.fields.Url,
          }]);
        })
        .catch(() => '');
    }
    return Promise.resolve(file);
  }, [uploadToStorage]);

  const handleFileUpload = useCallback((file: File) => {
    uploadFile(file, PRESIGNED_FILE_TYPES.task_attachment);
  }, [uploadFile]);

  const handleAttachmentsChange = useCallback((newAttachments: IFileUpload[]) => {
    setAttachmentsFiles(newAttachments);
  }, []);

  const startDate = useWatch({ control, name: TASK_START_DATE });
  const endDate = useWatch({ control, name: TASK_END_DATE });

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
      );
      end.setHours(
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
      );
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.floor(diffTime / (DATE_CALCULATIONS.MILLISECONDS_IN_DAY)) + DATE_CALCULATIONS.ONE_DAY;
      if (diffDays > 0) {
        setValue(TASK_DURATION, diffDays.toString());
      }
    } else {
      setValue(TASK_DURATION, '');
    }
  }, [startDate, endDate, setValue]);

  const handleStartDateChange = useCallback((date: TDate) => {
    if (date) {
      const newStartDate = new Date(date);
      newStartDate.setHours(
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
        DATE_CALCULATIONS.DEFAULT_TIMES,
      );

      if (endDate) {
        const currentEndDate = new Date(endDate);
        currentEndDate.setHours(
          DATE_CALCULATIONS.DEFAULT_TIMES,
          DATE_CALCULATIONS.DEFAULT_TIMES,
          DATE_CALCULATIONS.DEFAULT_TIMES,
          DATE_CALCULATIONS.DEFAULT_TIMES,
        );
        if (newStartDate.getTime() > currentEndDate.getTime()) {
          const newEndDate = new Date(newStartDate);
          newEndDate.setDate(newEndDate.getDate() + 1);
          setValue(TASK_END_DATE, newEndDate.toISOString().split('T')[0]);
        }
      } else {
        const newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + 1);
        setValue(TASK_END_DATE, newEndDate.toISOString().split('T')[0]);
      }
    }
  }, [endDate, setValue]);

  const handleEndDateChange = useCallback((date: TDate) => {
    if (date && !startDate) {
      setValue(TASK_START_DATE, new Date(date).toISOString().split('T')[0]);
    }
  }, [startDate, setValue]);

  const onCreateTask = handleSubmit((data) => {
    const {
      taskAssignedTo,
      taskDescription,
      taskDuration,
      taskEndDate,
      taskName,
      taskPriority,
      taskRelateTo,
      taskStartDate,
    } = data;

    const body: ITaskCreateRequest = {
      title: taskName,
      description: taskDescription || null,
      type: selectedTab === TAB_TYPES.VALUE_CHAIN ? TASK_TYPE.VCM : TASK_TYPE.MA,
      status: taskAssignedTo && taskAssignedTo.length > 0 ? TASK_STATUS.assigned : TASK_STATUS.created,
      priority: taskPriority?.value || 'low',
      duration: Number(taskDuration) || null,
      startDate: taskStartDate ? new Date(taskStartDate) : null,
      endDate: taskEndDate ? new Date(taskEndDate) : null,
      assignedUserIds: taskAssignedTo && taskAssignedTo?.length > 0
        ? taskAssignedTo?.map((user) => Number(user.value)) : [],
      attachments: attachmentsFiles,
    };

    if (selectedTab === TAB_TYPES.VALUE_CHAIN) {
      body.valueChainMapId = Number(taskRelateTo.value) > 0 ? Number(taskRelateTo.value) : null;
    } else {
      body.materialityAssessmentId = Number(taskRelateTo.value) > 0 ? Number(taskRelateTo.value) : null;
    }

    createTask(body)
      .then((dataResponse) => {
        if ('error' in dataResponse) {
          const errorMessage = extractErrorMessage(dataResponse);
          toast.error(errorMessage || t('common.unknownError'));
        } else {
          toast.success(t('taskManagement.taskHasBeenAdded'));
          onClose();
          reset();
          setAttachmentsFiles([]);
        }
      });
  });

  const onSaveAttachments = useCallback(() => {
    if (attachmentsFiles.filter((item) => !item.id).length > 0) {
      createFileAttachment({
        attachments: attachmentsFiles
          .filter((item) => !item.id)
          .map((item) => ({
            ...item,
          })),
        taskId: itemData?.id || 0,
      });
    }
  }, [attachmentsFiles, createFileAttachment, itemData?.id]);

  useEffect(() => {
    if (typeModal === TASK_TYPE_ACTION.EDIT && hideFieldsForm) {
      onSaveAttachments();
    }
  }, [typeModal, onSaveAttachments, hideFieldsForm]);

  useEffect(() => {
    if (itemData?.type && typeModal === TASK_TYPE_ACTION.EDIT) {
      setSelectedTab(itemData?.type === TASK_TYPE.VCM ? TAB_TYPES.VALUE_CHAIN : TAB_TYPES.MATERIALITY_ASSESSMENT);
    }
  }, [itemData?.type, typeModal]);

  const onUpdateTask = handleSubmit((data) => {
    const {
      taskAssignedTo,
      taskDescription,
      taskDuration,
      taskEndDate,
      taskName,
      taskPriority,
      taskRelateTo,
      taskStartDate,
    } = data;

    if (!hideFieldsForm) {
      const body: ITaskUpdateRequest = {
        id: itemData?.id || 0,
        data: {
          title: taskName,
          description: taskDescription || null,
          type: selectedTab === TAB_TYPES.VALUE_CHAIN ? TASK_TYPE.VCM : TASK_TYPE.MA,
          status: currentStatus || TASK_STATUS.created,
          priority: taskPriority?.value || 'low',
          duration: Number(taskDuration) || null,
          startDate: taskStartDate ? new Date(taskStartDate) : null,
          endDate: taskEndDate ? new Date(taskEndDate) : null,
        },
      };
      if (selectedTab === TAB_TYPES.VALUE_CHAIN) {
        body.data.valueChainMapId = Number(taskRelateTo.value) > 0 ? Number(taskRelateTo.value) : null;
      } else {
        body.data.materialityAssessmentId = Number(taskRelateTo.value) > 0 ? Number(taskRelateTo.value) : null;
      }

      updateTask(body).then((dataResponse) => {
        if ('error' in dataResponse) {
          const errorMessage = extractErrorMessage(dataResponse);
          toast.error(errorMessage || t('common.unknownError'));
        } else {
          toast.success(t('taskManagement.taskHasBeenAdded'));
          onClose();
          reset();
          setAttachmentsFiles([]);
        }
      });

      if (taskAssignedTo && taskAssignedTo?.length > 0) {
        updateSignToTask({
          id: itemData?.id || 0,
          userIds: taskAssignedTo?.map((user) => Number(user.value)),
        }).then(() => {
          onClose();
          reset();
          setAttachmentsFiles([]);
        });
      }
    }

    if (attachmentsFiles.filter((item) => !item.id).length > 0) {
      createFileAttachment({
        attachments: attachmentsFiles
          .filter((item) => !item.id)
          .map((item) => ({
            ...item,
          })),
        taskId: itemData?.id || 0,
      }).then(() => {
        onClose();
        reset();
        setAttachmentsFiles([]);
      });
    }
  });

  const handleDeleteFileAttachment = (idItem: number) => {
    deleteFileAttachment({ id: idItem });
  };

  const handleCloseModal = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      open={open}
      isButtonsVisible={false}
      className={styles.modal}
      title={typeModal === TASK_TYPE_ACTION.EDIT
        ? t('taskManagement.editTaskModalTitle') : t('taskManagement.createTaskModalTitle')}
      onClose={hideFieldsForm ? handleCloseModal : undefined}
      closeClassName={styles.closeButton}
    >
      <TaskFormFields
        control={control}
        modalRef={modalRef}
        hideFieldsForm={hideFieldsForm}
        t={t}
        onOpenVcToModal={onOpenVcToModal}
        onOpenUserModal={onOpenUserModal}
        handleStartDateChange={handleStartDateChange}
        handleEndDateChange={handleEndDateChange}
        startDate={startDate}
        attachmentsFiles={attachmentsFiles}
        handleAttachmentsChange={handleAttachmentsChange}
        handleFileUpload={handleFileUpload}
        userOwnerId={userData?.data?.id}
        typeModal={typeModal}
        handleDeleteFileAttachment={handleDeleteFileAttachment}
        accessDeleteFile={accessDeleteFile}
      />
      {!hideFieldsForm && (
        <div className={`${styles.buttonWrapper} ${hideFieldsForm ? styles.aloneButton : ''}`}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={handleCloseModal}
          />
          <Button
            title={typeModal === TASK_TYPE_ACTION.EDIT ? t('common.save') : t('common.add')}
            onClick={typeModal === TASK_TYPE_ACTION.EDIT ? onUpdateTask : onCreateTask}
            className={styles.button}
          />
        </div>
      )}
      {activeUserModal ? (
        <AddUserModal
          open={activeUserModal}
          onClose={onCloseUserModal}
          setValueForm={setValue}
          currentSelectedUsers={itemData?.assignedUsers?.map((user) => ({
            value: user?.id.toString(),
            label: `${user?.firstName} ${user?.lastName}`,
          })) || watch(TASK_ASSIGNED_TO)?.map((user) => ({
            value: user?.value || '',
            label: user?.label || '',
          }))}
          typeModal={typeModal}
        />
      ) : null}
      {activeVcToModal ? (
        <TaskRelatedModal
          open={activeVcToModal}
          onClose={onCloseVcToModal}
          setValueForm={setValue}
          selectedId={itemData?.valueChainMap?.id || Number(watch(TASK_RELATE_TO)?.value)}
          typeModal={typeModal}
          setError={setError}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      ) : null}
    </Modal>
  );
}

export default CreateTaskModal;
